import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Lock, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const from = location.state?.from?.pathname || '/account';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Header */}
      <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <Link to="/" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
          <h1 className="text-3xl font-black mb-2">Welcome Back</h1>
          <p className="text-neutral-400 mb-8">Sign in to your HDAlgos account</p>

          {error && (
            <div className="bg-red-950/30 ring-1 ring-red-900/50 rounded-lg p-4 mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-4 rounded-lg font-black text-lg hover:from-[#D8C585] hover:to-[#BDA45E] transition transform hover:scale-[1.02] mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {/* Register Link */}
            <p className="text-center text-sm text-neutral-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#CBB26A] hover:text-[#D8C585] font-bold">
                Create Account
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
